import { useState, useMemo } from "react";

/**
 * Modal for users to edit their own completed time tickets.
 * Limited to: duration, pieces finished, pieces scrapped.
 * When increasing duration, prompts for rework vs additional time.
 */
export default function TicketEditModal({ ticket, onSave, onClose }) {
  // Parse current duration into hours and minutes
  const initialSeconds = ticket.durationSeconds || 0;
  const [hours, setHours] = useState(Math.floor(initialSeconds / 3600));
  const [minutes, setMinutes] = useState(Math.floor((initialSeconds % 3600) / 60));
  const [piecesFinished, setPiecesFinished] = useState(ticket.piecesFinished ?? 0);
  const [piecesScrapped, setPiecesScrapped] = useState(ticket.piecesScrapped ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Step for rework prompt (null = editing, 'rework_prompt' = asking about rework)
  const [step, setStep] = useState(null);
  const [timeAdjustmentType, setTimeAdjustmentType] = useState(null); // 'additional' | 'rework'
  const [reworkReason, setReworkReason] = useState(null); // 'internal_error' | 'customer_request' | 'other'

  // Compute if anything changed and if duration increased
  const originalDuration = ticket.durationSeconds || 0;
  const newDuration = (hours * 3600) + (minutes * 60);
  const durationIncreased = newDuration > originalDuration;
  const hasChanges =
    newDuration !== originalDuration ||
    (parseInt(piecesFinished, 10) || 0) !== (ticket.piecesFinished ?? 0) ||
    (parseInt(piecesScrapped, 10) || 0) !== (ticket.piecesScrapped ?? 0);

  // Calculate added time for display
  const addedSeconds = Math.max(0, newDuration - originalDuration);
  const addedHours = Math.floor(addedSeconds / 3600);
  const addedMinutes = Math.floor((addedSeconds % 3600) / 60);

  const handleContinue = () => {
    if (durationIncreased && step === null) {
      // Show rework prompt before saving
      setStep('rework_prompt');
    } else {
      handleSave();
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);

    try {
      const durationSeconds = (hours * 3600) + (minutes * 60);
      await onSave(ticket.id, {
        durationSeconds,
        piecesFinished: parseInt(piecesFinished, 10) || 0,
        piecesScrapped: parseInt(piecesScrapped, 10) || 0,
        // Include rework info if duration was increased
        ...(durationIncreased && {
          timeAdjustmentType,
          reworkReason: timeAdjustmentType === 'rework' ? reworkReason : null,
        }),
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleTypeSelect = (type) => {
    setTimeAdjustmentType(type);
    if (type === 'additional') {
      setReworkReason(null);
    }
  };

  const canSubmitReworkStep = timeAdjustmentType === 'additional' ||
    (timeAdjustmentType === 'rework' && reworkReason);

  // Rework prompt step
  if (step === 'rework_prompt') {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.75)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 12,
            padding: 0,
            width: "100%",
            maxWidth: 420,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            padding: "16px 20px",
            borderBottom: "1px solid #fef3c7",
            backgroundColor: "#fffbeb",
            borderRadius: "12px 12px 0 0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 24 }}>⏱️</span>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
                  Time Adjustment
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: 13, color: "#92400e" }}>
                  Adding {addedHours > 0 ? `${addedHours}h ` : ''}{addedMinutes}m to this ticket
                </p>
              </div>
            </div>
          </div>

          {/* Job Info */}
          <div style={{ padding: "12px 20px", borderBottom: "1px solid #f3f4f6", backgroundColor: "#f9fafb" }}>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
              <span style={{ color: "#9ca3af" }}>Job:</span>{" "}
              <span style={{ fontWeight: 500, color: "#374151" }}>
                {ticket.jobNumber || ticket.jobId}
              </span>
            </p>
          </div>

          {/* Options */}
          <div style={{ padding: 20 }}>
            <p style={{ margin: "0 0 16px 0", fontSize: 14, color: "#374151" }}>
              What's the reason for adding time?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Adjust Time Option */}
              <button
                onClick={() => handleTypeSelect("additional")}
                style={{
                  width: "100%",
                  padding: 16,
                  borderRadius: 8,
                  border: timeAdjustmentType === "additional" ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                  backgroundColor: timeAdjustmentType === "additional" ? "#eff6ff" : "#fff",
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20 }}>➕</span>
                  <span style={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: timeAdjustmentType === "additional" ? "#1e40af" : "#374151"
                  }}>
                    Adjust Time
                  </span>
                </div>
              </button>

              {/* Rework Option */}
              <button
                onClick={() => handleTypeSelect("rework")}
                style={{
                  width: "100%",
                  padding: 16,
                  borderRadius: 8,
                  border: timeAdjustmentType === "rework" ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                  backgroundColor: timeAdjustmentType === "rework" ? "#eff6ff" : "#fff",
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20 }}>🔄</span>
                  <span style={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: timeAdjustmentType === "rework" ? "#1e40af" : "#374151"
                  }}>
                    Rework / Fix
                  </span>
                </div>
              </button>

              {/* Rework Reason Sub-Options */}
              {timeAdjustmentType === "rework" && (
                <div style={{
                  marginLeft: 24,
                  paddingLeft: 16,
                  borderLeft: "2px solid #bfdbfe",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: 12, color: "#6b7280" }}>
                    What caused the rework?
                  </p>

                  {/* Internal Error */}
                  <button
                    onClick={() => setReworkReason("internal_error")}
                    style={{
                      width: "100%",
                      padding: 12,
                      borderRadius: 6,
                      border: reworkReason === "internal_error" ? "2px solid #ef4444" : "1px solid #e5e7eb",
                      backgroundColor: reworkReason === "internal_error" ? "#fef2f2" : "#fff",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>🏭</span>
                      <div>
                        <span style={{
                          display: "block",
                          fontWeight: 500,
                          fontSize: 13,
                          color: reworkReason === "internal_error" ? "#b91c1c" : "#374151"
                        }}>
                          Internal Error
                        </span>
                        <span style={{
                          fontSize: 11,
                          color: reworkReason === "internal_error" ? "#dc2626" : "#6b7280"
                        }}>
                          Machine issue, operator error, etc.
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Customer Request */}
                  <button
                    onClick={() => setReworkReason("customer_request")}
                    style={{
                      width: "100%",
                      padding: 12,
                      borderRadius: 6,
                      border: reworkReason === "customer_request" ? "2px solid #f59e0b" : "1px solid #e5e7eb",
                      backgroundColor: reworkReason === "customer_request" ? "#fffbeb" : "#fff",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>👤</span>
                      <div>
                        <span style={{
                          display: "block",
                          fontWeight: 500,
                          fontSize: 13,
                          color: reworkReason === "customer_request" ? "#b45309" : "#374151"
                        }}>
                          Customer Request
                        </span>
                        <span style={{
                          fontSize: 11,
                          color: reworkReason === "customer_request" ? "#d97706" : "#6b7280"
                        }}>
                          Customer changed mind, artwork update, etc.
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Other */}
                  <button
                    onClick={() => setReworkReason("other")}
                    style={{
                      width: "100%",
                      padding: 12,
                      borderRadius: 6,
                      border: reworkReason === "other" ? "2px solid #6b7280" : "1px solid #e5e7eb",
                      backgroundColor: reworkReason === "other" ? "#f3f4f6" : "#fff",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>❓</span>
                      <div>
                        <span style={{
                          display: "block",
                          fontWeight: 500,
                          fontSize: 13,
                          color: reworkReason === "other" ? "#374151" : "#374151"
                        }}>
                          Other
                        </span>
                        <span style={{
                          fontSize: 11,
                          color: reworkReason === "other" ? "#4b5563" : "#6b7280"
                        }}>
                          Different reason
                        </span>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              margin: "0 20px 16px 20px",
              padding: 12,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              fontSize: 13,
              color: "#dc2626",
            }}>
              {error}
            </div>
          )}

          {/* Actions */}
          <div style={{
            padding: "16px 20px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            gap: 12,
            justifyContent: "flex-end",
          }}>
            <button
              onClick={() => setStep(null)}
              disabled={saving}
              style={{
                padding: "8px 16px",
                fontSize: 14,
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "none",
                borderRadius: 6,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              Back
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !canSubmitReworkStep}
              style={{
                padding: "8px 16px",
                fontSize: 14,
                backgroundColor: canSubmitReworkStep ? "#2563eb" : "#9ca3af",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: saving || !canSubmitReworkStep ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main edit form
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: 12,
          padding: 24,
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: "0 0 16px 0", fontSize: 18, color: "#3b82f6", fontWeight: 600 }}>
          Edit Time Ticket
        </h2>

        {/* Job Info (read-only) */}
        <div style={{ marginBottom: 20, padding: 12, backgroundColor: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Job</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: "#1f2937" }}>
            {ticket.jobNumber || ticket.jobId}
            {ticket.jobName && ` - ${ticket.jobName}`}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8, marginBottom: 4 }}>Operation</div>
          <div style={{ fontSize: 14, color: "#374151" }}>{ticket.operationCode || "—"}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8, marginBottom: 4 }}>Date</div>
          <div style={{ fontSize: 14, color: "#374151" }}>
            {ticket.startTime
              ? new Date(ticket.startTime).toLocaleString()
              : "—"}
          </div>
        </div>

        {/* Duration */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
            Duration
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number"
              min="0"
              value={hours}
              onChange={(e) => setHours(Math.max(0, parseInt(e.target.value, 10) || 0))}
              style={{
                width: 60,
                padding: "8px 10px",
                fontSize: 14,
                backgroundColor: "#fff",
                color: "#1f2937",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                textAlign: "center",
              }}
            />
            <span style={{ fontSize: 12, color: "#6b7280" }}>hours</span>
            <input
              type="number"
              min="0"
              max="59"
              value={minutes}
              onChange={(e) => setMinutes(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
              style={{
                width: 60,
                padding: "8px 10px",
                fontSize: 14,
                backgroundColor: "#fff",
                color: "#1f2937",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                textAlign: "center",
              }}
            />
            <span style={{ fontSize: 12, color: "#6b7280" }}>minutes</span>
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
            Original: {Math.floor(originalDuration / 3600)}h {Math.floor((originalDuration % 3600) / 60)}m
          </div>
        </div>

        {/* Pieces Finished */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
            Pieces Finished
          </label>
          <input
            type="number"
            min="0"
            value={piecesFinished}
            onChange={(e) => setPiecesFinished(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              fontSize: 14,
              backgroundColor: "#fff",
              color: "#1f2937",
              border: "1px solid #d1d5db",
              borderRadius: 6,
            }}
          />
        </div>

        {/* Pieces Scrapped */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
            Pieces Scrapped
          </label>
          <input
            type="number"
            min="0"
            value={piecesScrapped}
            onChange={(e) => setPiecesScrapped(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              fontSize: 14,
              backgroundColor: "#fff",
              color: "#1f2937",
              border: "1px solid #d1d5db",
              borderRadius: 6,
            }}
          />
        </div>

        {/* Sync Warning */}
        {(ticket.syncedToJobBoss === true || ticket.syncedToJobBoss === 1) && (
          <div
            style={{
              padding: 12,
              backgroundColor: "#fffbeb",
              border: "1px solid #fcd34d",
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 12,
              color: "#b45309",
            }}
          >
            This ticket was already synced to JobBOSS. Saving changes will mark it for re-sync.
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              padding: 12,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 12,
              color: "#dc2626",
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "8px 16px",
              fontSize: 14,
              backgroundColor: "#f3f4f6",
              color: "#374151",
              border: "none",
              borderRadius: 6,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={saving || !hasChanges}
            style={{
              padding: "8px 16px",
              fontSize: 14,
              backgroundColor: hasChanges ? "#2563eb" : "#9ca3af",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: saving || !hasChanges ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving..." : durationIncreased ? "Continue" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
